import React, { useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { ArrowRight, Bookmark, Globe2, LoaderCircle, Route, Server, ShieldCheck, X, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './ImportantNotice.css';
import { applyRouteNoticeEntry, generateAccessRouteLink, getRouteNoticeMutedUntil, readRouteNoticeEntry, ROUTE_NOTICE_MUTE_DURATION, setRouteNoticeMutedUntil } from '../../utils/routeNotice';
import useRouteSpeedTest from '../../hooks/useRouteSpeedTest';
import { ACCESS_ROUTES } from '../../config/accessRoutes';

const ROUTE_ICONS = { international: Globe2, us: Zap, hongKong: Server };
const ROUTE_ORIGINS = ACCESS_ROUTES.map(route => route.url);

const RouteNoticeDialog = ({ isLoggedIn = false, userToken }) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const confirmRef = useRef(null);
  const dismissedRef = useRef(false);
  const navigatingRef = useRef(false);
  const [navigating, setNavigating] = useState(null);
  const [mutedUntil, setMutedUntil] = useState(0);
  const { results, fastest, start, cancel } = useRouteSpeedTest(ROUTE_ORIGINS);

  useEffect(() => {
    start();
  }, [start]);

  useEffect(() => {
    const resetNavigation = () => {
      navigatingRef.current = false;
      setNavigating(null);
    };
    window.addEventListener('pageshow', resetNavigation);
    // 导航未完成或被浏览器取消时，允许用户再次点击。
    const timer = navigating ? window.setTimeout(resetNavigation, 15000) : null;
    return () => {
      window.removeEventListener('pageshow', resetNavigation);
      window.clearTimeout(timer);
    };
  }, [navigating]);
  const visible = typeof window !== 'undefined';

  useEffect(() => {
    if (!visible || dismissedRef.current) return undefined;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    confirmRef.current.focus({ preventScroll: true });
    document.body.style.overflow = 'hidden';
    const onClose = () => {
      if (dialog.open) return;
      dismissedRef.current = true;
      cancel();
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
    dialog.addEventListener('close', onClose);
    return () => {
      dialog.removeEventListener('close', onClose);
      dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, [visible, cancel]);

  if (!visible) return null;
  const close = () => dialogRef.current.close();
  const hasToken = isLoggedIn && typeof userToken === 'string' && userToken.trim();
  const handleNavigate = (event, destination) => {
    if (navigatingRef.current) {
      event.preventDefault();
      return;
    }
    // 保留浏览器 Ctrl/Cmd 点击新标签页的原生行为。
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    navigatingRef.current = true;
    cancel();
    flushSync(() => setNavigating(destination));
  };
  const renderRoute = (route) => {
    const destination = generateAccessRouteLink(route.url, { isLoggedIn, userToken, mutedUntil });
    const busy = navigating === destination;
    const domain = new URL(route.url).hostname;
    const Icon = ROUTE_ICONS[route.descriptionKey] || Server;
    const result = results[route.url];
    // 资源加载时间：500 ms 内为绿，1500 ms 内为黄，更慢或失败为红。
    const speedLevel = !result || result.status === 'testing' ? 'pending'
      : result.status !== 'success' ? 'slow'
      : result.milliseconds <= 500 ? 'fast' : result.milliseconds <= 1500 ? 'moderate' : 'slow';
    const speedText = result?.status === 'success'
      ? t('importantNotice.speed.elapsed', { milliseconds: result.milliseconds })
      : result?.status === 'timeout' ? t('importantNotice.speed.timeout') : '— ms';
    const speedLabel = result?.status === 'success'
      ? t('importantNotice.speed.label', { milliseconds: result.milliseconds })
      : t(`importantNotice.speed.${result?.status || 'testing'}`);
    return (
      <article key={route.url} className={`important-notice-route${route.recommended ? ' is-primary' : ''}`}>
        <div className="important-notice-route-top">
          <span className="important-notice-route-icon"><Icon size={21} strokeWidth={1.7} aria-hidden="true" /></span>
          {route.recommended && <span className="important-notice-badge">{t('importantNotice.recommended')}</span>}
        </div>
        <div className="important-notice-route-info">
          <h3>{t(`importantNotice.routes.${route.descriptionKey}`)}</h3>
          <p className="important-notice-domain">{domain}</p>
          <div className={`important-notice-speed-result is-${speedLevel}`} title={speedLabel} aria-label={speedLabel}>
            <span className="important-notice-speed-dot" aria-hidden="true" />
            <span>{speedText}</span>
            {fastest === route.url && <strong>{t('importantNotice.speed.fastest')}</strong>}
          </div>
        </div>
        <a className="important-notice-go" href={destination} referrerPolicy="no-referrer"
          aria-label={t('importantNotice.visitRoute', { route: t(`importantNotice.routes.${route.descriptionKey}`), domain })}
          onClick={event => handleNavigate(event, destination)} aria-busy={busy} aria-disabled={Boolean(navigating)}>
          <span>{t(busy ? 'importantNotice.navigating' : 'importantNotice.directVisit')}</span>
          {busy ? <LoaderCircle size={16} className="important-notice-spinner" aria-hidden="true" /> : <ArrowRight size={16} aria-hidden="true" />}
        </a>
      </article>
    );
  };
  return createPortal(
    <dialog ref={dialogRef} className="important-notice" aria-labelledby="important-notice-title" aria-describedby="important-notice-description">
      <div className="important-notice-heading">
        <div className="important-notice-eyebrow"><Route size={14} aria-hidden="true" />{t('importantNotice.eyebrow')}</div>
        <h2 id="important-notice-title">{t('importantNotice.title')}</h2>
        <p id="important-notice-description">{t('importantNotice.content')}</p>
        <button type="button" className="important-notice-close" onClick={close} aria-label={t('importantNotice.close')}><X size={18} /></button>
      </div>
      <div className="important-notice-body">
        <section aria-labelledby="notice-routes-title">
          <div className="important-notice-section-heading">
            <h3 id="notice-routes-title">{t('importantNotice.routesTitle')}</h3>
            <span><ShieldCheck size={13} aria-hidden="true" />{t(hasToken ? 'importantNotice.signedInHint' : 'importantNotice.visitHint')}</span>
          </div>
          <div className="important-notice-featured">{ACCESS_ROUTES.filter(route => route.recommended).map(renderRoute)}</div>
          <div className="important-notice-other-routes">{ACCESS_ROUTES.filter(route => !route.recommended).map(renderRoute)}</div>
          <p className="important-notice-vpn-hint">{t('importantNotice.vpnHint')}</p>
        </section>
      </div>
      <div className="important-notice-footer">
        <div className="important-notice-footer-copy">
          <label className="important-notice-remember">
            <input type="checkbox" checked={Boolean(mutedUntil)} onChange={event => {
              setMutedUntil(setRouteNoticeMutedUntil(event.target.checked ? Date.now() + ROUTE_NOTICE_MUTE_DURATION : 0));
            }} />
            <span>{t('importantNotice.remember')}</span>
          </label>
          <p><Bookmark size={14} aria-hidden="true" />{t('importantNotice.bookmark')}</p>
        </div>
        <span className="sr-only" role="status">{navigating ? t('importantNotice.navigating') : ''}</span>
        <button type="button" ref={confirmRef} className="important-notice-stay" onClick={close}>{t('importantNotice.confirm')}</button>
      </div>
    </dialog>, document.body
  );
};

export default function ImportantNotice({ entry, ...props }) {
  const [initialEntry] = useState(() => entry || readRouteNoticeEntry(typeof window === 'undefined' ? '' : window.location.search));
  const [visible] = useState(() => typeof window !== 'undefined'
    && !initialEntry.skip && !initialEntry.mutedUntil && !getRouteNoticeMutedUntil());

  useEffect(() => {
    applyRouteNoticeEntry(initialEntry);
  }, [initialEntry]);

  return visible ? <RouteNoticeDialog {...props} /> : null;
}
